import { hashKey, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getCreditStatsKey } from '~/swr-hooks/workspaces/useCreditStats';

type CreditStatsBroadcastMessage = {
  workspaceId: string;
  data: CreditUsageStats;
  dataUpdatedAt: number;
};

const CREDIT_STATS_BROADCAST_CHANNEL_NAME = 'CREDIT_STATS_BROADCAST_CHANNEL';

const creditStatsChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CREDIT_STATS_BROADCAST_CHANNEL_NAME) : undefined;

/**
 * Syncs credit stats data across browser tabs via BroadcastChannel.
 * When the credit stats query cache updates in one tab, this broadcasts the data
 * to other tabs which update their local cache without making additional network requests.
 */
export const useBroadcastCreditStats = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  // Broadcast credit stats to other tabs when the query cache updates
  useEffect(() => {
    if (!creditStatsChannel || !workspaceId) {
      return;
    }

    const queryHash = hashKey(getCreditStatsKey({ workspaceId }));

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'success' &&
        !event.action.manual &&
        event.query.queryHash === queryHash
      ) {
        const message: CreditStatsBroadcastMessage = {
          workspaceId,
          data: event.query.state.data,
          dataUpdatedAt: event.query.state.dataUpdatedAt,
        };
        creditStatsChannel.postMessage(message);
      }
    });

    return unsubscribe;
  }, [queryClient, workspaceId]);

  // Listen for credit stats from other tabs
  useEffect(() => {
    if (!creditStatsChannel || !workspaceId) {
      return;
    }

    const handleMessage = (event: MessageEvent<CreditStatsBroadcastMessage>) => {
      if (event.data?.workspaceId !== workspaceId) {
        return;
      }

      const queryKey = getCreditStatsKey({ workspaceId });
      const localDataUpdatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0;

      if (event.data.dataUpdatedAt > localDataUpdatedAt) {
        queryClient.setQueryData(queryKey, event.data.data, { updatedAt: event.data.dataUpdatedAt });
      }
    };

    creditStatsChannel.addEventListener('message', handleMessage);

    return () => {
      creditStatsChannel.removeEventListener('message', handleMessage);
    };
  }, [queryClient, workspaceId]);
};
